import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Switch, CircularProgress, Alert,
  Button, Dialog, DialogTitle, DialogContent, MenuItem,
  DialogActions, TextField, useTheme, IconButton, Tooltip, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Archive as ArchiveIcon, Unarchive as UnarchiveIcon
} from '@mui/icons-material';
import tcDocumentTypeService from '../../services/tc-document-type.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { isPrimaryDocType } from '../../utils/docTypeGroups';
import { can } from '../../utils/roleUtils';

const f = { xs: '0.75rem', sm: '0.8125rem' };

// Add a custom type, or edit an existing one when `docType` is given. The code is the type's
// identity (stored on uploaded documents, used for extraction and required-doc matching), so it is
// read-only once created.
const AddDocTypeDialog = ({ open, onClose, onSave, docType = null }) => {
  const isEdit = !!docType;
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('Other');
  const [description, setDescription] = useState('');
  const [holderType, setHolderType] = useState('EMPLOYEE');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCode(docType?.code || '');
    setLabel(docType?.label || '');
    setCategory(docType?.category || 'Other');
    setDescription(docType?.description || '');
    setHolderType(docType?.holder_type || docType?.holderType || 'EMPLOYEE');
    setSaveError('');
  }, [open, docType]);

  const handleSave = async () => {
    if (!label.trim()) return;
    setSaving(true);
    try {
      await onSave(isEdit
        ? { label, category, description: description || null, holder_type: holderType }
        : { code: code || label.toUpperCase().replace(/[^A-Z0-9]/g, '_'), label, category, description: description || null, holder_type: holderType });
      onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.response?.data?.error || 'Could not save the document type');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: f.sm }}>{isEdit ? 'Edit Document Type' : 'Add Custom Document Type'}</DialogTitle>
      <DialogContent>
        {saveError && <Alert severity="error" sx={{ mt: 1, fontSize: f.xs }}>{saveError}</Alert>}
        <TextField fullWidth label="Label" value={label} onChange={e => setLabel(e.target.value)} sx={{ mt: 1, mb: 1.5 }} size="small" required InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }} />
        <TextField fullWidth label={isEdit ? 'Code (cannot be changed)' : 'Code (auto-generated if blank)'} value={code} onChange={e => setCode(e.target.value)} disabled={isEdit} sx={{ mb: 1.5 }} size="small" InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }} placeholder="e.g. HEALTH_CARD" />
        <TextField select fullWidth label="For" value={holderType} onChange={e => setHolderType(e.target.value)} sx={{ mb: 1.5 }} size="small" InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }}>
          <MenuItem value="EMPLOYEE" sx={{ fontSize: f.sm }}>Employee</MenuItem>
          <MenuItem value="ORGANIZATION" sx={{ fontSize: f.sm }}>Company</MenuItem>
        </TextField>
        <TextField fullWidth label="Category" value={category} onChange={e => setCategory(e.target.value)} sx={{ mb: 1.5 }} size="small" InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }} />
        <TextField fullWidth label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} size="small" multiline rows={2} InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontSize: f.xs }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !label.trim()} sx={{ fontSize: f.xs }}>
          {isEdit ? (saving ? 'Saving...' : 'Save') : (saving ? 'Adding...' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DocumentTypeSettings = ({ refreshKey = 0, onSeed, seeding, canWrite: canWriteFromParent }) => {
  // Session user for permission checks (this page receives no currentUser prop).
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const theme = useTheme();
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Reloads on mount and whenever the parent seeds default types (refreshKey bump).
  useEffect(() => { loadData(); }, [refreshKey]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); }
  }, [success]);

  const loadData = async () => {
    setLoading(true);
    try {
      const types = await tcDocumentTypeService.getAll();
      setDocTypes(types);
    } catch (err) {
      setError('Failed to load document types');
    } finally { setLoading(false); }
  };

  // Mark a document type as primary/secondary. Primary types are grouped first in every doc picker.
  const handleTogglePrimary = async (dt) => {
    try {
      const next = !isPrimaryDocType(dt);
      await tcDocumentTypeService.update(dt.id, { is_primary: next });
      setDocTypes(prev => prev.map(d => d.id === dt.id ? { ...d, is_primary: next } : d));
      setSuccess(`${dt.label} set as ${next ? 'primary' : 'secondary'}`);
    } catch (err) {
      setError('Failed to update');
    }
  };

  const handleAdd = async (data) => {
    await tcDocumentTypeService.create(data);
    setSuccess('Document type added');
    loadData();
  };

  const handleEdit = async (data) => {
    await tcDocumentTypeService.update(editTarget.id, data);
    setSuccess('Document type updated');
    loadData();
  };

  // Archive (status DISABLED) hides a type from every doc picker; restore brings it back. The server
  // refuses to archive a type an active service still requires and says which services.
  const handleToggleArchive = async (dt) => {
    const archiving = dt.status !== 'DISABLED';
    try {
      await tcDocumentTypeService.update(dt.id, { status: archiving ? 'DISABLED' : 'ACTIVE' });
      setDocTypes(prev => prev.map(d => d.id === dt.id ? { ...d, status: archiving ? 'DISABLED' : 'ACTIVE' } : d));
      setSuccess(`${dt.label} ${archiving ? 'archived' : 'restored'}`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || `Could not ${archiving ? 'archive' : 'restore'} ${dt.label}`);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  // Group by category
  const categories = [...new Set(docTypes.map(d => d.category || 'Other'))];
  const canWrite = can(sessionUser, 'service_catalog', 'write');
  const activeTypes = docTypes.filter(d => d.status !== 'DISABLED');
  const visibleTypes = showArchived ? docTypes : activeTypes;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>
          Document Types ({activeTypes.filter(isPrimaryDocType).length} primary / {activeTypes.length} total)
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <FormControlLabel
            control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
            label={<Typography sx={{ fontSize: f.xs }}>Show archived</Typography>}
          />
          {onSeed && canWriteFromParent && (
            <Button variant="outlined" size="small" onClick={onSeed} disabled={seeding}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
              {seeding ? 'Seeding…' : 'Seed'}
            </Button>
          )}
          {can(sessionUser, 'service_catalog', 'write') && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
              Add Doc Type
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: f.xs }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontSize: f.xs }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={getBrandedTableHeadSx(theme)}>
            <TableRow sx={{ '& th': { fontSize: f.xs } }}>
              <TableCell>Code</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="center">For</TableCell>
              <TableCell align="center">Source</TableCell>
              <TableCell align="center">Primary</TableCell>
              <TableCell align="center">Status</TableCell>
              {canWrite && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTypes.map(dt => (
              <TableRow key={dt.id} hover sx={{ opacity: dt.status === 'DISABLED' ? 0.5 : 1 }}>
                <TableCell>
                  <Typography sx={{ fontSize: f.xs, fontFamily: 'monospace' }}>{dt.code}</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: f.sm }}>{dt.label}</Typography>
                  {dt.description && <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>{dt.description}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: f.xs }}>{dt.category || '—'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: f.xs }}>
                    {(dt.holder_type || dt.holderType) === 'ORGANIZATION' ? 'Company' : 'Employee'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={dt.source === 'CUSTOM' ? 'Custom' : 'Default'}
                    size="small"
                    color={dt.source === 'CUSTOM' ? 'info' : 'default'}
                    sx={{ fontSize: '0.6rem', height: 18 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={isPrimaryDocType(dt)}
                    disabled={!can(sessionUser, 'service_catalog', 'write')}
                    onChange={() => handleTogglePrimary(dt)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={dt.status === 'DISABLED' ? 'Archived' : 'Active'}
                    size="small"
                    color={dt.status === 'DISABLED' ? 'default' : 'success'}
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 18 }}
                  />
                </TableCell>
                {canWrite && (
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditTarget(dt)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={dt.status === 'DISABLED' ? 'Restore (show in document pickers)' : 'Archive (hide from document pickers)'}>
                      <IconButton size="small" color={dt.status === 'DISABLED' ? 'primary' : 'error'} onClick={() => handleToggleArchive(dt)}>
                        {dt.status === 'DISABLED' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddDocTypeDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} onSave={handleAdd} />
      <AddDocTypeDialog open={!!editTarget} docType={editTarget} onClose={() => setEditTarget(null)} onSave={handleEdit} />
    </Box>
  );
};

export default DocumentTypeSettings;
